import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MAIN_SERVICES, SUPPORT_SERVICES } from "@/data/content";

export default function Services() {
  const scrollTo = (sel) => {
    const el = document.querySelector(sel);
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="services" data-testid="services-section" className="relative py-32 lg:py-44 px-6 lg:px-24">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-20 lg:mb-28">
          <div>
            <p className="font-mono text-[10px] tracking-[0.35em] text-goldw uppercase mb-6">Chapter 02 — Services</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.05]" data-testid="services-headline">
              Premium Services,<br /><span className="italic text-goldw">Precisely Delivered</span>
            </h2>
          </div>
          <p className="text-sm text-zinc2 max-w-sm leading-relaxed">
            Four signature disciplines, one standard: spaces that feel inevitable the moment you walk in.
          </p>
        </div>

        <div>
          {MAIN_SERVICES.map((s, i) => (
            <motion.article
              key={s.num}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`service-row-${s.num}`}
              className="group grid lg:grid-cols-12 gap-8 lg:gap-12 items-center py-12 lg:py-16 border-t border-white/8"
            >
              <div className="lg:col-span-1">
                <span className="font-mono text-sm text-goldw/70 tracking-[0.2em]">{s.num}</span>
              </div>
              <div className="lg:col-span-5">
                <h3 className="font-serif font-light text-3xl sm:text-4xl text-ivory group-hover:text-goldw transition-colors duration-500">
                  {s.title}
                </h3>
                <p className="mt-4 text-sm sm:text-base text-zinc2 leading-relaxed max-w-md">{s.copy}</p>
                <button
                  data-testid={`service-quote-${s.num}`}
                  onClick={() => scrollTo("#contact")}
                  className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-goldw/80 hover:text-goldw transition-colors"
                >
                  Discuss this project <ArrowUpRight size={14} />
                </button>
              </div>
              <div className="lg:col-span-6">
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img
                    src={s.image}
                    alt={s.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 border border-white/5 pointer-events-none" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 border border-white/8">
          {SUPPORT_SERVICES.map((s, i) => (
            <motion.div
              key={s}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.6 }}
              data-testid={`support-service-${i}`}
              className="px-4 py-6 text-center font-mono text-[10px] tracking-[0.18em] uppercase text-zinc2 border-white/8 [&:not(:last-child)]:border-b sm:[&:not(:last-child)]:border-b lg:border-b-0 sm:[&:nth-child(2n)]:border-l lg:[&:not(:first-child)]:border-l hover:text-goldw hover:bg-white/[0.02] transition-colors duration-300"
            >
              {s}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
